import React, { useState } from "react";
import { toast } from "sonner";
import { generateForm, exportFormDocx, authLogout, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ModeNav from "@/components/ModeNav";
import BrandMark from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sparkles, FileDown, Loader2, ListChecks, Plus, Trash2, LogOut, CircleDot, AlignLeft, Info, ArrowUp, ArrowDown,
} from "lucide-react";

const ACCENT = "#002FA7";

const FormBuilder = () => {
  const { user, isAdmin, logout } = useAuth();
  const [title, setTitle] = useState("");
  const [topic, setTopic] = useState("");
  const [numQuestions, setNumQuestions] = useState("8");
  const [style, setStyle] = useState("mixed");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleLogout = async () => { try { await authLogout(); } catch (e) {} logout(); };

  const handleGenerate = async () => {
    if (!topic.trim()) { toast.error("Describe the topic or paste notes first."); return; }
    setGenerating(true);
    const t = toast.loading("Claude is drafting your questions…");
    try {
      const res = await generateForm({ topic, num_questions: parseInt(numQuestions), style, title: title || null });
      setTitle(res.title);
      setDescription(res.description || "");
      setQuestions(res.questions);
      toast.success("Questions generated", { id: t });
    } catch (e) {
      toast.error(apiError(e), { id: t });
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    if (questions.length === 0) { toast.error("Generate some questions first."); return; }
    setExporting(true);
    try {
      await exportFormDocx({ title: title || "Questionnaire", description, questions });
      toast.success("Word file downloaded — ready for Microsoft Forms");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const updateQ = (i, patch) => setQuestions(questions.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const moveQ = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[i], next[j]] = [next[j], next[i]];
    setQuestions(next);
  };
  const addQ = () => setQuestions([...questions, { type: "multiple_choice", text: "New question", options: ["Option 1", "Option 2"] }]);
  const updateOpt = (qi, oi, val) => updateQ(qi, { options: questions[qi].options.map((o, idx) => idx === oi ? val : o) });
  const addOpt = (qi) => updateQ(qi, { options: [...questions[qi].options, `Option ${questions[qi].options.length + 1}`] });
  const removeOpt = (qi, oi) => updateQ(qi, { options: questions[qi].options.filter((_, idx) => idx !== oi) });

  return (
    <div className="h-screen w-full flex overflow-hidden bg-slate-100 text-slate-900">
      {/* Sidebar */}
      <aside className="w-full md:w-[420px] lg:w-[460px] h-full flex flex-col border-r border-slate-200 bg-white z-10 shrink-0">
        <div className="px-6 py-5 border-b border-slate-200 flex items-center gap-2.5">
          <BrandMark height={24} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 border-l border-slate-200 pl-2.5 leading-tight">Form<br />Builder</div>
        </div>

        <ModeNav accent={ACCENT} />

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <Label className="label-caps">Form Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Auto-generated if left blank" className="mt-1.5" data-testid="form-title-input" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-caps">Questions</Label>
              <Select value={numQuestions} onValueChange={setNumQuestions}>
                <SelectTrigger className="mt-1.5" data-testid="form-count-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["5", "8", "10", "15", "20"].map((n) => <SelectItem key={n} value={n}>{n} questions</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps">Style</Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="mt-1.5" data-testid="form-style-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mixed">Mixed</SelectItem>
                  <SelectItem value="multiple_choice">Multiple choice</SelectItem>
                  <SelectItem value="open_text">Open text</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="label-caps">Topic / Notes</Label>
            <Textarea value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Describe what the questionnaire is for, or paste rough notes. e.g. 'Screening questions for a junior electrician role — safety awareness, experience, availability.'" className="mt-1.5 min-h-[160px] text-sm leading-relaxed resize-none" data-testid="form-topic-input" />
          </div>

          <Button onClick={handleGenerate} disabled={generating} className="w-full gap-2 text-white transition-all hover:-translate-y-[1px] hover:brightness-110" style={{ background: ACCENT }} data-testid="form-generate-btn">
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? "Drafting…" : "Generate Questions"}
          </Button>

          {/* Editable questions */}
          {questions.length > 0 && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <Label className="label-caps">Questions ({questions.length})</Label>
                <Button variant="outline" size="sm" onClick={addQ} className="gap-1.5 h-7 text-xs" data-testid="form-add-question"><Plus size={13} /> Add</Button>
              </div>
              {questions.map((q, i) => (
                <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2 bg-white" data-testid={`form-q-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 w-5">{i + 1}.</span>
                    <Select value={q.type} onValueChange={(v) => updateQ(i, { type: v, options: v === "open_text" ? [] : (q.options.length ? q.options : ["Option 1", "Option 2"]) })}>
                      <SelectTrigger className="h-7 w-[140px] text-xs" data-testid={`form-q-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="multiple_choice" className="text-xs">Multiple choice</SelectItem>
                        <SelectItem value="open_text" className="text-xs">Open text</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex-1" />
                    <button onClick={() => moveQ(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid={`form-q-up-${i}`}><ArrowUp size={13} /></button>
                    <button onClick={() => moveQ(i, 1)} disabled={i === questions.length - 1} className="text-slate-400 hover:text-slate-800 disabled:opacity-30" data-testid={`form-q-down-${i}`}><ArrowDown size={13} /></button>
                    <button onClick={() => removeQ(i)} className="text-slate-400 hover:text-red-600" data-testid={`form-q-remove-${i}`}><Trash2 size={14} /></button>
                  </div>
                  <Textarea value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })} className="text-sm min-h-[52px] resize-none" data-testid={`form-q-text-${i}`} />
                  {q.type === "multiple_choice" && (
                    <div className="space-y-1.5 pl-2">
                      {q.options.map((o, oi) => (
                        <div key={oi} className="flex items-center gap-2">
                          <CircleDot size={13} className="text-slate-300 shrink-0" />
                          <Input value={o} onChange={(e) => updateOpt(i, oi, e.target.value)} className="h-7 text-xs" data-testid={`form-q-${i}-opt-${oi}`} />
                          <button onClick={() => removeOpt(i, oi)} className="text-slate-300 hover:text-red-600" data-testid={`form-q-${i}-opt-remove-${oi}`}><Trash2 size={13} /></button>
                        </div>
                      ))}
                      <button onClick={() => addOpt(i)} className="text-xs text-[#002FA7] hover:underline pl-5" data-testid={`form-q-${i}-add-opt`}>+ add option</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User bar */}
        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ background: ACCENT }}>
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate leading-tight">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400">{isAdmin ? "Administrator" : "Member"}</div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleLogout} className="h-8 w-8 text-slate-400 hover:text-red-600" data-testid="logout-btn" title="Sign out"><LogOut size={16} /></Button>
        </div>
      </aside>

      {/* Preview */}
      <main className="flex-1 h-full flex flex-col overflow-hidden">
        <div className="h-16 border-b border-slate-200 bg-white px-8 flex items-center justify-between shrink-0">
          <span className="label-caps">Microsoft Forms Export</span>
          <Button onClick={handleExport} disabled={exporting} className="gap-2 text-white transition-all hover:-translate-y-[1px] hover:brightness-110" style={{ background: ACCENT }} data-testid="form-export-btn">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
            Download for Microsoft Forms (.docx)
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-100 p-8 lg:p-12">
          <div className="w-full max-w-[760px] mx-auto space-y-6">
            {/* Import instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3" data-testid="form-instructions">
              <Info size={18} className="text-[#002FA7] shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700 leading-relaxed">
                <span className="font-semibold text-[#002FA7]">How to use:</span> Download the .docx, then in Microsoft Forms go to
                <span className="font-medium"> New Form → Quick Import</span>, upload this file (max 10MB), review the detected questions, and publish. Multiple-choice and open-text questions import automatically.
              </div>
            </div>

            {/* Preview card */}
            <div className="bg-white border border-slate-200 shadow-[0_8px_40px_rgb(0,0,0,0.08)] rounded-lg overflow-hidden" data-testid="form-preview">
              <div className="h-2" style={{ background: ACCENT }} />
              <div className="p-8">
                <h1 className="font-doc text-3xl font-bold text-slate-900" data-testid="form-preview-title">{title || "Your questionnaire"}</h1>
                {description && <p className="text-sm text-slate-500 mt-2">{description}</p>}

                {questions.length === 0 ? (
                  <div className="text-slate-400 text-sm py-16 text-center border border-dashed border-slate-200 rounded-md mt-6">
                    Describe a topic and click <span className="font-semibold">Generate Questions</span> to draft a form for candidates.
                  </div>
                ) : (
                  <div className="mt-6 space-y-6">
                    {questions.map((q, i) => (
                      <div key={i} className="border-b border-slate-100 pb-5">
                        <div className="flex gap-2 text-slate-800 font-medium text-[15px]">
                          <span className="text-[#002FA7]">{i + 1}.</span>
                          <span>{q.text}</span>
                        </div>
                        {q.type === "multiple_choice" ? (
                          <div className="mt-3 space-y-2 pl-5">
                            {q.options.map((o, oi) => (
                              <div key={oi} className="flex items-center gap-2.5 text-sm text-slate-600">
                                <span className="h-4 w-4 rounded-full border-2 border-slate-300 shrink-0" />
                                {o}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="mt-3 pl-5 flex items-center gap-2 text-sm text-slate-400">
                            <AlignLeft size={15} /> <span className="border-b border-dashed border-slate-300 flex-1 pb-1">Long answer text</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default FormBuilder;
